import { authClient } from "@/lib/auth-client";
import { AlertTriangle, Copy, Download, ShieldCheck } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { useState } from "react";
import { toast } from "sonner";

type EnrollState =
	| { kind: "idle" }
	| { kind: "password" }
	| { kind: "show-qr"; totpUri: string; backupCodes: string[] };

export function EnrollmentFlow({
	onEnrolled,
}: {
	onEnrolled: (codes: string[]) => void;
}) {
	const [state, setState] = useState<EnrollState>({ kind: "idle" });

	return (
		<div className="rounded-lg border border-[#e5e7eb] bg-white p-6">
			<div className="mb-4 flex items-start gap-3">
				<AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#f59e0b]" strokeWidth={1.5} />
				<div>
					<h2 className="text-sm font-semibold text-[#1e1e20]">Two-factor isn't set up yet</h2>
					<p className="mt-1 text-sm text-[#6b7280]">
						Enroll an authenticator app (recommended) to get a 6-digit code each time you sign in.
						You can also request the code by email when logging in.
					</p>
				</div>
			</div>

			{state.kind === "idle" && (
				<button
					type="button"
					onClick={() => setState({ kind: "password" })}
					className="h-9 rounded bg-[#16a34a] px-4 text-sm font-semibold text-white hover:bg-[#15803d]"
				>
					Set up two-factor
				</button>
			)}

			{state.kind === "password" && (
				<PasswordToEnable
					onBack={() => setState({ kind: "idle" })}
					onEnabled={(res) =>
						setState({ kind: "show-qr", totpUri: res.totpURI, backupCodes: res.backupCodes })
					}
				/>
			)}

			{state.kind === "show-qr" && (
				<VerifyTotpStep
					totpUri={state.totpUri}
					backupCodes={state.backupCodes}
					onVerified={() => onEnrolled(state.backupCodes)}
				/>
			)}
		</div>
	);
}

function PasswordToEnable({
	onBack,
	onEnabled,
}: {
	onBack: () => void;
	onEnabled: (res: { totpURI: string; backupCodes: string[] }) => void;
}) {
	const [password, setPassword] = useState("");
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	async function handleSubmit(e: React.FormEvent) {
		e.preventDefault();
		setError(null);
		setLoading(true);
		try {
			const result = await authClient.twoFactor.enable({ password });
			if (result.error) {
				setError(result.error.message ?? "Failed to enable 2FA");
				return;
			}
			if (!result.data) {
				setError("Unexpected response from server");
				return;
			}
			onEnabled({
				totpURI: result.data.totpURI,
				backupCodes: result.data.backupCodes,
			});
		} catch (err: unknown) {
			setError(err instanceof Error ? err.message : "Failed to enable 2FA");
		} finally {
			setLoading(false);
		}
	}

	return (
		<form onSubmit={handleSubmit} className="max-w-sm space-y-4">
			<div>
				<h3 className="text-sm font-medium text-[#1e1e20]">Confirm your password</h3>
				<p className="mt-1 text-xs text-[#6b7280]">
					For security we ask for your password before enabling 2FA.
				</p>
			</div>
			{error && <div className="rounded bg-[#fef2f2] p-3 text-sm text-[#dc2626]">{error}</div>}
			<div className="space-y-1">
				<label htmlFor="enable-password" className="text-xs font-medium text-[#1e1e20]">
					Password
				</label>
				<input
					id="enable-password"
					type="password"
					value={password}
					onChange={(e) => setPassword(e.target.value)}
					required
					autoComplete="current-password"
					className="h-9 w-full rounded border border-[#d1d5db] bg-white px-3 text-sm text-[#1e1e20] focus:border-[#4f46e5] focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20"
				/>
			</div>
			<div className="flex items-center gap-2">
				<button
					type="submit"
					disabled={loading || !password}
					className="h-9 rounded bg-[#16a34a] px-4 text-sm font-semibold text-white hover:bg-[#15803d] disabled:opacity-50"
				>
					{loading ? "Continuing…" : "Continue"}
				</button>
				<button
					type="button"
					onClick={onBack}
					className="h-9 rounded border border-[#d1d5db] bg-white px-4 text-sm font-medium text-[#6b7280] hover:bg-[#f8f9fb]"
				>
					Cancel
				</button>
			</div>
		</form>
	);
}

function VerifyTotpStep({
	totpUri,
	onVerified,
}: {
	totpUri: string;
	backupCodes: string[];
	onVerified: () => void;
}) {
	const [code, setCode] = useState("");
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const secret = extractSecret(totpUri);

	async function handleSubmit(e: React.FormEvent) {
		e.preventDefault();
		setError(null);
		setLoading(true);
		try {
			const result = await authClient.twoFactor.verifyTotp({ code });
			if (result.error) {
				setError(result.error.message ?? "Invalid code");
				return;
			}
			onVerified();
		} catch (err: unknown) {
			setError(err instanceof Error ? err.message : "Invalid code");
		} finally {
			setLoading(false);
		}
	}

	return (
		<div className="space-y-6">
			<div>
				<h3 className="text-sm font-medium text-[#1e1e20]">Scan with your authenticator app</h3>
				<p className="mt-1 text-xs text-[#6b7280]">
					Open your authenticator (Google Authenticator, 1Password, Authy, etc.) and scan this code.
					Then enter the 6-digit code it shows.
				</p>
			</div>

			<div className="flex items-start gap-6">
				<div className="rounded-lg border border-[#e5e7eb] bg-white p-4">
					<QRCodeSVG value={totpUri} size={160} level="M" />
				</div>

				<div className="flex-1 space-y-2">
					<p className="text-xs font-medium text-[#6b7280]">Or enter this secret manually:</p>
					<div className="flex items-center gap-2">
						<code className="flex-1 break-all rounded bg-[#f8f9fb] px-2 py-1.5 font-mono text-xs text-[#1e1e20]">
							{secret}
						</code>
						<button
							type="button"
							onClick={() => {
								void navigator.clipboard.writeText(secret);
								toast.success("Secret copied");
							}}
							className="rounded border border-[#d1d5db] bg-white p-1.5 text-[#6b7280] hover:bg-[#f8f9fb]"
							title="Copy secret"
						>
							<Copy className="h-3.5 w-3.5" strokeWidth={1.5} />
						</button>
					</div>
				</div>
			</div>

			<form onSubmit={handleSubmit} className="max-w-sm space-y-4">
				{error && <div className="rounded bg-[#fef2f2] p-3 text-sm text-[#dc2626]">{error}</div>}
				<div className="space-y-1">
					<label htmlFor="totp-code" className="text-xs font-medium text-[#1e1e20]">
						Enter the 6-digit code
					</label>
					<input
						id="totp-code"
						type="text"
						inputMode="numeric"
						pattern="[0-9]{6}"
						maxLength={6}
						value={code}
						onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
						required
						className="h-10 w-40 rounded border border-[#d1d5db] bg-white px-3 text-center text-lg font-mono tracking-[0.3em] text-[#1e1e20] focus:border-[#4f46e5] focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/20"
					/>
				</div>
				<button
					type="submit"
					disabled={loading || code.length !== 6}
					className="h-9 rounded bg-[#16a34a] px-4 text-sm font-semibold text-white hover:bg-[#15803d] disabled:opacity-50"
				>
					{loading ? "Verifying…" : "Verify and enable"}
				</button>
			</form>
		</div>
	);
}

export function BackupCodesStep({
	codes,
	onDone,
}: {
	codes: string[];
	onDone: () => void;
}) {
	const [saved, setSaved] = useState(false);

	const codesText = codes.join("\n");

	function handleDownload() {
		const blob = new Blob([codesText], { type: "text/plain" });
		const url = URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = "payrollmart-backup-codes.txt";
		a.click();
		URL.revokeObjectURL(url);
	}

	function handleCopy() {
		void navigator.clipboard.writeText(codesText);
		toast.success("Backup codes copied");
	}

	return (
		<div className="space-y-6">
			<div className="flex items-start gap-3">
				<ShieldCheck className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#16a34a]" strokeWidth={1.5} />
				<div>
					<h3 className="text-sm font-semibold text-[#1e1e20]">
						2FA is enabled. Save your backup codes.
					</h3>
					<p className="mt-1 text-sm text-[#6b7280]">
						Use one of these if you lose access to your authenticator. Each code works once.
						<strong className="font-semibold"> You won't see these again.</strong>
					</p>
				</div>
			</div>

			<div className="rounded-lg border border-[#e5e7eb] bg-[#f8f9fb] p-4">
				<ul className="grid grid-cols-2 gap-x-8 gap-y-1.5 font-mono text-sm text-[#1e1e20]">
					{codes.map((c) => (
						<li key={c}>{c}</li>
					))}
				</ul>
			</div>

			<div className="flex items-center gap-2">
				<button
					type="button"
					onClick={handleDownload}
					className="inline-flex h-9 items-center gap-1.5 rounded border border-[#d1d5db] bg-white px-3 text-sm font-medium text-[#1e1e20] hover:bg-[#f8f9fb]"
				>
					<Download className="h-3.5 w-3.5" strokeWidth={1.5} />
					Download .txt
				</button>
				<button
					type="button"
					onClick={handleCopy}
					className="inline-flex h-9 items-center gap-1.5 rounded border border-[#d1d5db] bg-white px-3 text-sm font-medium text-[#1e1e20] hover:bg-[#f8f9fb]"
				>
					<Copy className="h-3.5 w-3.5" strokeWidth={1.5} />
					Copy
				</button>
			</div>

			<label className="flex items-start gap-2 text-sm text-[#1e1e20]">
				<input
					type="checkbox"
					checked={saved}
					onChange={(e) => setSaved(e.target.checked)}
					className="mt-0.5 h-4 w-4 rounded border-[#d1d5db] text-[#16a34a] focus:ring-[#4f46e5]"
				/>
				I've saved these codes somewhere safe.
			</label>

			<button
				type="button"
				onClick={onDone}
				disabled={!saved}
				className="h-9 rounded bg-[#16a34a] px-4 text-sm font-semibold text-white hover:bg-[#15803d] disabled:opacity-50"
			>
				Done
			</button>
		</div>
	);
}

// Extract the `secret` param from an otpauth://totp/... URI for manual entry.
function extractSecret(totpUri: string): string {
	try {
		const url = new URL(totpUri);
		return url.searchParams.get("secret") ?? "";
	} catch {
		return "";
	}
}
